import React from "react";
import PropTypes from "prop-types";
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import HistoryIcon from "@mui/icons-material/History";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLoginDetails } from "../../store/UserStore/action";
import OnitLogo from "../ServicesStatus/onitLogo.png";
import { PersonAddAlt1 } from "@mui/icons-material";

const drawerWidth = 240;
const collapsedDrawerWidth = 73;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ...theme.mixins.toolbar,
}));

const drawerItems = [
  { name: "Profile", path: "/client-profile", icon: <AccountBoxIcon /> },
  { name: "Requests", path: "/client-requests", icon: <RequestQuoteIcon /> },
  {
    name: "Add Request",
    path: "/add-client-request-by-id",
    icon: <PersonAddAlt1 />,
  },
  { name: "Payment History", path: "/payment-history", icon: <HistoryIcon /> },
  { name: "Part Request", path: "/part-request", icon: <RequestQuoteIcon /> },
  // { name: "Report", path: "/client-report", icon: <HistoryIcon /> },
];

function ClientNavBar(props) {
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const globalState = useSelector((state) => state.clientReducer);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const onClickLogout = () => {
    dispatch(
      setLoginDetails({
        userDetails: {},
        isUserLoggedIn: false,
        userTokens: { accessToken: "" },
      })
    );
    navigate("/add-client-request-by-id");
  };

  const drawer = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "linear-gradient(to bottom, #0d9488, #0f766e)",
        // borderTopRightRadius: "0rem",
        borderBottomRightRadius: "1rem",
        overflow: "hidden",
        color: "white",
      }}
    >
      <DrawerHeader>
        <img
          src={OnitLogo}
          alt="Onit Logo"
          style={{
            width: sidebarOpen ? "70%" : "65%",
            height: "70px",
            margin: isMobile ? "60px auto" : "20px auto 10px",
            objectFit: "contain",
            transition: "all 0.3s ease",
            filter: "drop-shadow(0px 2px 6px rgba(0,0,0,0.3))",
          }}
        />
      </DrawerHeader>

      <List
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": { width: "0.3em" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#14b8a6",
            borderRadius: "6px",
          },
        }}
      >
        {drawerItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem
              button
              key={item.name}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                my: 1,
                height: "48px",
                borderRadius: "12px",
                backgroundColor: isActive ? "#14b8a6" : "transparent",
                "&:hover": {
                  backgroundColor: "#14b8a6",
                  boxShadow: "0px 4px 10px rgba(20,184,166,0.4)",
                },
                justifyContent: sidebarOpen ? "initial" : "center",
                px: sidebarOpen ? 2.5 : 1,
                transition: "all 0.3s ease",
                color: isActive ? "#fff" : "#e0f2f1",
                cursor: "pointer",
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: "center",
                  color: "inherit",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {sidebarOpen && (
                <ListItemText
                  primary={item.name}
                  sx={{
                    ml: 2,
                    fontWeight: isActive ? "600" : "400",
                  }}
                />
              )}
            </ListItem>
          );
        })}
      </List>

      {/* Logout Button */}
      <List sx={{ flexShrink: 0 }}>
        <ListItem
          button
          onClick={onClickLogout}
          sx={{
            my: 1,
            height: "48px",
            borderRadius: "12px",
            color: "#fff",
            "&:hover": { backgroundColor: "#dc2626" },
            justifyContent: sidebarOpen ? "initial" : "center",
            px: sidebarOpen ? 2.5 : 1,
            transition: "all 0.3s ease",
            cursor: "pointer",
          }}
        >
          <ListItemIcon
            sx={{ minWidth: 0, justifyContent: "center", color: "inherit" }}
          >
            <LogoutIcon />
          </ListItemIcon>
          {sidebarOpen && <ListItemText primary="Logout" sx={{ ml: 2 }} />}
        </ListItem>
      </List>

      {/* Toggle button */}
      {!isMobile && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton
            onClick={toggleSidebar}
            size="small"
            sx={{ color: "white" }}
          >
            {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {/* Futuristic AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: {
            xs: "100%",
            sm: `calc(100% - ${
              sidebarOpen ? drawerWidth : collapsedDrawerWidth
            }px)`,
          },
          ml: {
            xs: 0,
            sm: `${sidebarOpen ? drawerWidth : collapsedDrawerWidth}px`,
          },
          background: "linear-gradient(to right, #0f766e, #115e59)",
          boxShadow: "0px 2px 10px rgba(0,0,0,0.2)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          borderBottomLeftRadius: "0.50rem",
        }}
        elevation={0}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" }, color: "white" }}
          >
            <MenuIcon />
          </IconButton>

          {/* Title */}
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ color: "white", fontWeight: "600" }}
          >
            ONIT-SERVICES (अब हर काम बहुत आसान)
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          {/* Right Side User */}
          <Box sx={{ display: "flex", alignItems: "center", color: "white" }}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ mr: 1, fontWeight: "500" }}
            >
              {globalState?.clientDetails?.client_name || "Client"}
            </Typography>
            <AccountBoxIcon sx={{ fontSize: 36, rounded: "100%" }} />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{
          width: { sm: sidebarOpen ? drawerWidth : collapsedDrawerWidth },
          flexShrink: { sm: 0 },
        }}
        aria-label="client navigation"
      >
        {/* Mobile Drawer */}
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Permanent Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: sidebarOpen ? drawerWidth : collapsedDrawerWidth,
              overflowX: "hidden",
              background: "transparent",
              borderRight: "none",
              transition: (theme) =>
                theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );
}

ClientNavBar.propTypes = {
  window: PropTypes.func,
};

export default ClientNavBar;
